import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  BookOpen,
  Headphones,
  PenTool,
  Mic,
  Plus,
  Edit2,
  Trash2,
  Upload,
  Sparkles,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const API_URL = window.location.origin;

const QuestionBankManager = () => {
  const { isDark } = useAdminTheme();
  
  // State
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Filters
  const [testTypeFilter, setTestTypeFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [formData, setFormData] = useState({
    test_type: 'ielts',
    section: 'reading',
    question_type: 'mcq_single',
    difficulty: 'medium',
    content: { text: '', passage: '', options: [], correct_answer: '' },
    time_limit: 60
  });
  
  // Generate AI settings
  const [generateSettings, setGenerateSettings] = useState({
    test_type: 'ielts',
    section: 'reading',
    count: 5
  });
  
  // Import data
  const [importJson, setImportJson] = useState('');

  useEffect(() => {
    fetchQuestions();
    fetchStats();
  }, [testTypeFilter, sectionFilter, page]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/english-test/admin/questions?limit=${limit}&skip=${(page - 1) * limit}`;
      if (testTypeFilter !== 'all') url += `&test_type=${testTypeFilter}`;
      if (sectionFilter !== 'all') url += `&section=${sectionFilter}`;
      
      const res = await fetch(url);
      const data = await res.json();
      
      if (data.success) {
        setQuestions(data.questions);
        setTotalPages(Math.ceil(data.total / limit));
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/english-test/admin/stats`);
      const data = await res.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSaveQuestion = async () => {
    try {
      const url = selectedQuestion 
        ? `${API_URL}/api/english-test/admin/questions/${selectedQuestion.id}`
        : `${API_URL}/api/english-test/admin/questions`;
      
      const method = selectedQuestion ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (data.success) {
        fetchQuestions();
        fetchStats();
        setShowAddDialog(false);
        setShowEditDialog(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error saving question:', error);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!selectedQuestion) return;
    
    try {
      const res = await fetch(`${API_URL}/api/english-test/admin/questions/${selectedQuestion.id}`, {
        method: 'DELETE'
      });
      
      const data = await res.json();
      
      if (data.success) {
        fetchQuestions();
        fetchStats();
        setShowDeleteDialog(false);
        setSelectedQuestion(null);
      }
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const handleGenerateAI = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/english-test/admin/questions/generate-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(generateSettings)
      });
      
      const data = await res.json();
      
      if (data.success) {
        alert(`Successfully generated ${data.generated} questions!`);
        fetchQuestions();
        fetchStats();
        setShowGenerateDialog(false);
      } else {
        alert('Failed to generate questions: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      alert('Error generating questions');
    } finally {
      setGenerating(false);
    }
  };

  const handleBulkImport = async () => {
    try {
      const questions = JSON.parse(importJson);
      
      const res = await fetch(`${API_URL}/api/english-test/admin/questions/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions: Array.isArray(questions) ? questions : [questions] })
      });
      
      const data = await res.json();
      
      if (data.success) {
        alert(`Imported ${data.imported} questions!`);
        fetchQuestions();
        fetchStats();
        setShowImportDialog(false);
        setImportJson('');
      }
    } catch (error) {
      alert('Invalid JSON format');
    }
  };

  const resetForm = () => {
    setFormData({
      test_type: 'ielts',
      section: 'reading',
      question_type: 'mcq_single',
      difficulty: 'medium',
      content: { text: '', passage: '', options: [], correct_answer: '' },
      time_limit: 60
    });
    setSelectedQuestion(null);
  };

  const openEditDialog = (question) => {
    setSelectedQuestion(question);
    setFormData({
      test_type: question.test_type,
      section: question.section,
      question_type: question.question_type,
      difficulty: question.difficulty || 'medium',
      content: question.content || {},
      time_limit: question.time_limit || 60
    });
    setShowEditDialog(true);
  };

  const getSectionIcon = (section) => {
    const icons = {
      reading: BookOpen,
      listening: Headphones,
      writing: PenTool,
      speaking: Mic,
      speaking_writing: Mic
    };
    const Icon = icons[section] || BookOpen;
    return <Icon className="w-4 h-4" />;
  };

  const getSectionColor = (section) => {
    const colors = {
      reading: 'bg-blue-500',
      listening: 'bg-purple-500',
      writing: 'bg-green-500',
      speaking: 'bg-orange-500',
      speaking_writing: 'bg-pink-500'
    };
    return colors[section] || 'bg-gray-500';
  };

  const getQuestionPreview = (question) => {
    if (question.content?.text) return question.content.text.substring(0, 80) + '...';
    if (question.content?.passage) return question.content.passage.substring(0, 80) + '...';
    if (question.content?.audio_text) return question.content.audio_text.substring(0, 80) + '...';
    return 'No preview available';
  };

  const filteredQuestions = questions.filter(q => {
    if (!searchQuery) return true;
    const preview = getQuestionPreview(q).toLowerCase();
    return preview.includes(searchQuery.toLowerCase());
  });

  return (
    <AdminLayout>
      <div className={`min-h-screen p-6 ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Question Bank Manager
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage IELTS & PTE practice test questions
            </p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button 
              variant="outline" 
              onClick={() => setShowImportDialog(true)}
              className={isDark ? 'border-white/20 text-white' : ''}
            >
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowGenerateDialog(true)}
              className={`${isDark ? 'border-purple-500/50 text-purple-400 hover:bg-purple-500/10' : 'border-purple-500 text-purple-600 hover:bg-purple-50'}`}
            >
              <Sparkles className="w-4 h-4 mr-2" /> Generate with AI
            </Button>
            <Button onClick={() => { resetForm(); setShowAddDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Add Question
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
            <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
              <CardContent className="p-4 text-center">
                <div className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>{stats.total}</div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total</div>
              </CardContent>
            </Card>
            {Object.entries(stats.by_section).map(([key, value]) => (
              <Card key={key} className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                <CardContent className="p-4 text-center">
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>{value}</div>
                  <div className={`text-xs capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {key.replace('_', ' ')}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <Card className={`mb-6 ${isDark ? 'bg-[#233842] border-white/10' : ''}`}>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <Input
                    placeholder="Search questions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-10 ${isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}`}
                  />
                </div>
              </div>
              <Select value={testTypeFilter} onValueChange={setTestTypeFilter}>
                <SelectTrigger className={`w-[150px] ${isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}`}>
                  <SelectValue placeholder="Test Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tests</SelectItem>
                  <SelectItem value="ielts">IELTS</SelectItem>
                  <SelectItem value="pte">PTE</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sectionFilter} onValueChange={setSectionFilter}>
                <SelectTrigger className={`w-[180px] ${isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}`}>
                  <SelectValue placeholder="Section" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sections</SelectItem>
                  <SelectItem value="reading">Reading</SelectItem>
                  <SelectItem value="listening">Listening</SelectItem>
                  <SelectItem value="writing">Writing</SelectItem>
                  <SelectItem value="speaking">Speaking</SelectItem>
                  <SelectItem value="speaking_writing">Speaking & Writing</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchQuestions} className={isDark ? 'border-white/20' : ''}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Questions Table */}
        <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className={`w-6 h-6 animate-spin ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'border-white/10' : ''}>
                    <TableHead className={isDark ? 'text-gray-400' : ''}>Test</TableHead>
                    <TableHead className={isDark ? 'text-gray-400' : ''}>Section</TableHead>
                    <TableHead className={isDark ? 'text-gray-400' : ''}>Type</TableHead>
                    <TableHead className={isDark ? 'text-gray-400' : ''}>Preview</TableHead>
                    <TableHead className={isDark ? 'text-gray-400' : ''}>Difficulty</TableHead>
                    <TableHead className={`text-right ${isDark ? 'text-gray-400' : ''}`}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredQuestions.map((question) => (
                    <TableRow key={question.id} className={isDark ? 'border-white/10' : ''}>
                      <TableCell>
                        <Badge variant={question.test_type === 'ielts' ? 'default' : 'secondary'}>
                          {question.test_type.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${getSectionColor(question.section)}`}>
                            {getSectionIcon(question.section)}
                          </div>
                          <span className={`capitalize ${isDark ? 'text-white' : ''}`}>
                            {question.section.replace('_', ' ')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className={isDark ? 'text-gray-300' : ''}>
                        {question.question_type.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell className={`max-w-xs truncate ${isDark ? 'text-gray-300' : ''}`}>
                        {getQuestionPreview(question)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          question.difficulty === 'hard' ? 'border-red-500 text-red-500' :
                          question.difficulty === 'easy' ? 'border-green-500 text-green-500' :
                          'border-amber-500 text-amber-500'
                        }>
                          {question.difficulty || 'medium'}
                        </Badge>
                        {question.ai_generated && (
                          <Badge variant="outline" className="ml-2 border-purple-500 text-purple-500">
                            <Sparkles className="w-3 h-3 mr-1" /> AI
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(question)}
                          className={isDark ? 'text-gray-400 hover:text-white' : ''}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedQuestion(question); setShowDeleteDialog(true); }}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className={isDark ? 'border-white/20' : ''}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className={isDark ? 'border-white/20' : ''}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Add/Edit Question Dialog */}
        <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
          if (!open) { setShowAddDialog(false); setShowEditDialog(false); resetForm(); }
        }}>
          <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#233842] border-white/10' : ''}`}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : ''}>
                {selectedQuestion ? 'Edit Question' : 'Add New Question'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : ''}`}>Test Type</label>
                  <Select value={formData.test_type} onValueChange={(v) => setFormData({...formData, test_type: v})}>
                    <SelectTrigger className={isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ielts">IELTS</SelectItem>
                      <SelectItem value="pte">PTE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : ''}`}>Section</label>
                  <Select value={formData.section} onValueChange={(v) => setFormData({...formData, section: v})}>
                    <SelectTrigger className={isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reading">Reading</SelectItem>
                      <SelectItem value="listening">Listening</SelectItem>
                      <SelectItem value="writing">Writing</SelectItem>
                      <SelectItem value="speaking">Speaking</SelectItem>
                      <SelectItem value="speaking_writing">Speaking & Writing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : ''}`}>Question Type</label>
                  <Select value={formData.question_type} onValueChange={(v) => setFormData({...formData, question_type: v})}>
                    <SelectTrigger className={isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mcq_single">MCQ Single</SelectItem>
                      <SelectItem value="mcq_multiple">MCQ Multiple</SelectItem>
                      <SelectItem value="true_false_ng">True/False/NG</SelectItem>
                      <SelectItem value="fill_blank">Fill Blank</SelectItem>
                      <SelectItem value="fill_blank_dropdown">Fill Blank Dropdown</SelectItem>
                      <SelectItem value="reorder_paragraphs">Reorder Paragraphs</SelectItem>
                      <SelectItem value="listening_mcq">Listening MCQ</SelectItem>
                      <SelectItem value="listening_fill_blank">Listening Fill Blank</SelectItem>
                      <SelectItem value="essay">Essay</SelectItem>
                      <SelectItem value="summarize_text">Summarize Text</SelectItem>
                      <SelectItem value="speaking_part1">Speaking Part 1</SelectItem>
                      <SelectItem value="speaking_part2">Speaking Part 2</SelectItem>
                      <SelectItem value="read_aloud">Read Aloud</SelectItem>
                      <SelectItem value="repeat_sentence">Repeat Sentence</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : ''}`}>Difficulty</label>
                  <Select value={formData.difficulty} onValueChange={(v) => setFormData({...formData, difficulty: v})}>
                    <SelectTrigger className={isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : ''}`}>Question Content (JSON)</label>
                <Textarea
                  value={JSON.stringify(formData.content, null, 2)}
                  onChange={(e) => {
                    try {
                      setFormData({...formData, content: JSON.parse(e.target.value)});
                    } catch {}
                  }}
                  className={`min-h-[200px] font-mono text-sm ${isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}`}
                  placeholder='{"text": "Question text...", "options": [...]}'
                />
              </div>
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : ''}`}>Time Limit (seconds)</label>
                <Input
                  type="number"
                  value={formData.time_limit}
                  onChange={(e) => setFormData({...formData, time_limit: parseInt(e.target.value)})}
                  className={isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAddDialog(false); setShowEditDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleSaveQuestion}>
                {selectedQuestion ? 'Update' : 'Create'} Question
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : ''}>Delete Question</DialogTitle>
              <DialogDescription className={isDark ? 'text-gray-400' : ''}>
                Are you sure you want to delete this question? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteQuestion}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Generate AI Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                <Sparkles className="w-5 h-5 text-purple-500" /> Generate Questions with AI
              </DialogTitle>
              <DialogDescription className={isDark ? 'text-gray-400' : ''}>
                AI will generate new questions based on existing patterns in your question bank.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : ''}`}>Test Type</label>
                <Select value={generateSettings.test_type} onValueChange={(v) => setGenerateSettings({...generateSettings, test_type: v})}>
                  <SelectTrigger className={isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ielts">IELTS</SelectItem>
                    <SelectItem value="pte">PTE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : ''}`}>Section</label>
                <Select value={generateSettings.section} onValueChange={(v) => setGenerateSettings({...generateSettings, section: v})}>
                  <SelectTrigger className={isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reading">Reading</SelectItem>
                    <SelectItem value="listening">Listening</SelectItem>
                    <SelectItem value="writing">Writing</SelectItem>
                    <SelectItem value="speaking">Speaking</SelectItem>
                    <SelectItem value="speaking_writing">Speaking & Writing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : ''}`}>Number of Questions (max 10)</label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={generateSettings.count}
                  onChange={(e) => setGenerateSettings({...generateSettings, count: Math.min(10, parseInt(e.target.value) || 1)})}
                  className={isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
              <Button onClick={handleGenerateAI} disabled={generating} className="bg-purple-600 hover:bg-purple-700">
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" /> Generate
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className={`max-w-2xl ${isDark ? 'bg-[#233842] border-white/10' : ''}`}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : ''}>Import Questions</DialogTitle>
              <DialogDescription className={isDark ? 'text-gray-400' : ''}>
                Paste JSON array of questions to import.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Textarea
                value={importJson}
                onChange={(e) => setImportJson(e.target.value)}
                placeholder='[{"test_type": "ielts", "section": "reading", "question_type": "mcq_single", ...}]'
                className={`min-h-[300px] font-mono text-sm ${isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''}`}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancel</Button>
              <Button onClick={handleBulkImport}>
                <Upload className="w-4 h-4 mr-2" /> Import
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      </div>
    </AdminLayout>
  );
};

export default QuestionBankManager;
